// libraries.
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

// api.
import {
  apiAcquisitionsPortal,
  apiDocumentsHubSpot,
  apiDocumentsPortal,
  apiInventoryPortal,
  apiPricingPortal,
  apiSellersPortal,
  apiOrchestratorPricingCommittee,
} from './apiSlices/apiSlices';

// domains.
import { domainLeadDetailSlice } from '@domains/lead-detail/store/slices/lead-detail-slice';
import { apiAuth, apiAuthV2, domainUserAuthSlice } from '@domains/user';
import { domainPolygonToolSlice } from '@domains/polygon-tool';
import { domainMedianZonesSlice } from '@domains/median-zones';
import { domainModePolygonSlice } from '@domains/save-polygons';
import { domainIntersectionsSlice } from '@domains/intersections';
import { domainDocumentReviewSlice } from '@domains/documents-review';
import { domainWebSocketSlice, domainHermesDataSlice } from '@domains/hermes';
import { domainOrchestratorPricingCommitteeSlice } from '@domains/orchestrator-pricing-committee/store/slices/domainOrchestratorPricingCommittee.slice';
import { domainGeoreferencingSlice } from '@pages/DocumentAuditPage/modules/Georeferencing/store/slices/georeferencing-slice';

// ui.
import { uiSidebarSlice } from '@shared/Sidebar/store/slices/sidebar-slice';

const persistConfig = {
  key: 'store_global',
  storage,
  whitelist: ['domainUserAuth', 'domainDocumentReview'],
};

export const rootReducer = combineReducers({
  // apis.
  [apiAcquisitionsPortal.reducerPath]: apiAcquisitionsPortal.reducer,
  [apiDocumentsHubSpot.reducerPath]: apiDocumentsHubSpot.reducer,
  [apiInventoryPortal.reducerPath]: apiInventoryPortal.reducer,
  [apiDocumentsPortal.reducerPath]: apiDocumentsPortal.reducer,
  [apiSellersPortal.reducerPath]: apiSellersPortal.reducer,
  [apiPricingPortal.reducerPath]: apiPricingPortal.reducer,
  [apiAuth.reducerPath]: apiAuth.reducer,
  [apiAuthV2.reducerPath]: apiAuthV2.reducer,
  [apiOrchestratorPricingCommittee.reducerPath]:
    apiOrchestratorPricingCommittee.reducer,
  // domains
  domainUserAuth: domainUserAuthSlice.reducer,
  domainLeadDetail: domainLeadDetailSlice.reducer,
  domainHermesData: domainHermesDataSlice.reducer,
  domainPolygonTool: domainPolygonToolSlice.reducer,
  domainMedianZones: domainMedianZonesSlice.reducer,
  domainModePolygon: domainModePolygonSlice.reducer,
  domainIntersections: domainIntersectionsSlice.reducer,
  domainDocumentReview: domainDocumentReviewSlice.reducer,
  domainWebSocket: domainWebSocketSlice.reducer,
  domainOrchestratorPricingCommittee:
    domainOrchestratorPricingCommitteeSlice.reducer,
  domainGeoreferencing: domainGeoreferencingSlice.reducer,
  // ui
  uiSidebar: uiSidebarSlice.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

// middlewares.
export const middlewares = [
  apiAcquisitionsPortal.middleware,
  apiDocumentsHubSpot.middleware,
  apiInventoryPortal.middleware,
  apiDocumentsPortal.middleware,
  apiSellersPortal.middleware,
  apiPricingPortal.middleware,
  apiAuth.middleware,
  apiAuthV2.middleware,
  apiOrchestratorPricingCommittee.middleware,
];

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        inmutableCheck: false,
        serializableCheck: false,
      },
    }).concat(middlewares),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
